/* fowm — 3D: the pouch (built from the real packaging artwork), the drinks and the scoop-to-foam story.
   One transparent canvas sits over the page; each [data-stage] element tells it where to draw what. */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

const FW = (window.fowm = window.fowm || {});
const doc = document.documentElement;
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const canvas = document.getElementById('fw-gl');
const stageEls = [...document.querySelectorAll('[data-stage]')];
const fail = () => { doc.classList.add('fw-no-3d'); if (canvas) canvas.style.display = 'none'; };

const C = {
  ink: '#633a23', inkTex: '#7a4a2f', foam: '#f3e7d6', foamShade: '#e6d3bb', cream: '#efe3d2',
  espresso: '#2e170b', crema: '#7a4526', coffee: '#4a2716', milk: '#efe4d4', latte: '#b78a62', cocoa: '#6e4026'
};

async function main() {
  if (!canvas || !stageEls.length) return;
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    if (!renderer.getContext()) throw new Error('no context');
  } catch (e) { fail(); return; }
  const small = innerWidth < 760;
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, small ? 1.75 : 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 100);
  camera.position.set(0, 0, 12);

  /* ---------- studio light ---------- */
  {
    const env = new THREE.Scene();
    env.add(new THREE.Mesh(new THREE.SphereGeometry(30, 32, 16), new THREE.MeshBasicMaterial({ color: new THREE.Color('#8a6a55'), side: THREE.BackSide })));
    const panel = (w, h, x, y, z, col) => { const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(col), side: THREE.DoubleSide })); m.position.set(x, y, z); m.lookAt(0, 0, 0); env.add(m); };
    panel(12, 7, -4, 12, 8, '#fff6ea'); panel(5, 16, -15, 2, 6, '#ffe9d2'); panel(6, 16, 15, 3, -4, '#f2d6bb'); panel(22, 4, 0, -12, 6, '#5a3825'); panel(4, 4, 8, 7, 13, '#ffffff');
    const pm = new THREE.PMREMGenerator(renderer);
    scene.environment = pm.fromScene(env, 0.04).texture;
    pm.dispose();
  }
  scene.add(new THREE.HemisphereLight(0xfffaf4, 0xa98466, 0.7));
  const key = new THREE.DirectionalLight(0xfff9f3, 1.35); key.position.set(-4, 6, 8); scene.add(key);
  const rim = new THREE.DirectionalLight(0xffe6cf, 0.85); rim.position.set(6, 3, -6); scene.add(rim);
  const fill = new THREE.DirectionalLight(0xffffff, 0.35); fill.position.set(5, -2, 6); scene.add(fill);

  /* ---------- helpers ---------- */
  const mk = (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };
  const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));
  const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t); };
  const lerp = (a, b, t) => a + (b - a) * t;
  const ease = t => 1 - Math.pow(1 - clamp(t), 3);
  const V3 = (x, y, z) => new THREE.Vector3(x, y, z);
  const rnd = s => () => { s |= 0; s = (s + 0x6d2b79f5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const loadImg = src => new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const canvasTex = (c, srgb = true) => { const t = new THREE.CanvasTexture(c); if (srgb) t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy()); return t; };
  const radial = (inner, outer) => { const c = mk(128, 128), g = c.getContext('2d'); const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64); gr.addColorStop(0, inner); gr.addColorStop(1, outer); g.fillStyle = gr; g.fillRect(0, 0, 128, 128); return canvasTex(c); };
  const shadowTex = radial('rgba(40,20,10,0.55)', 'rgba(40,20,10,0)');
  function contactShadow(w, d) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false, opacity: 0.8 }));
    m.rotation.x = -Math.PI / 2; m.renderOrder = -1; return m;
  }
  // a soft bumpy texture for foam and powder
  function noiseTex(size, cells, seed, base = 128, amp = 90) {
    const c = mk(size, size), g = c.getContext('2d'), r = rnd(seed);
    g.fillStyle = `rgb(${base},${base},${base})`; g.fillRect(0, 0, size, size);
    for (let i = 0; i < cells; i++) {
      const x = r() * size, y = r() * size, rad = (0.01 + r() * 0.035) * size, v = base + (r() - 0.35) * amp;
      const gr = g.createRadialGradient(x, y, 0, x, y, rad); gr.addColorStop(0, `rgba(${v},${v},${v},0.9)`); gr.addColorStop(1, `rgba(${v},${v},${v},0)`);
      g.fillStyle = gr;
      for (const dx of [-size, 0, size]) for (const dy of [-size, 0, size]) { g.beginPath(); g.arc(x + dx, y + dy, rad, 0, 7); g.fill(); }
    }
    const t = canvasTex(c, false); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t;
  }
  const foamBump = noiseTex(256, 420, 7);
  const powderBump = noiseTex(128, 260, 11, 140, 70);

  /* ---------- the pouch ---------- */
  const PW = 1.6, PH = PW * 1045 / 840; // matches the artwork's aspect ratio
  async function buildPouch() {
    const [foil, labelSrc, silSrc] = await Promise.all([loadImg(FW.assets.foil), fetch(FW.assets.label).then(r => r.text()), fetch(FW.assets.silhouette).then(r => r.text())]);
    const tw = small ? 1024 : 1536, th = Math.round(tw * 1045 / 840);
    const svgImg = src => loadImg(URL.createObjectURL(new Blob([src.replace('<svg ', `<svg width="${tw}" height="${th}" `)], { type: 'image/svg+xml' })));
    const [label, sil] = await Promise.all([svgImg(labelSrc), svgImg(silSrc)]);
    const printed = (drawInk, mirror) => {
      const c = mk(tw, th), g = c.getContext('2d');
      if (mirror) { g.save(); g.translate(tw, 0); g.scale(-1, 1); }
      g.drawImage(foil, 0, 0, tw, th);
      if (mirror) g.restore();
      const L = mk(tw, th), l = L.getContext('2d');
      drawInk(l);
      l.globalCompositeOperation = 'source-in'; l.fillStyle = C.inkTex; l.fillRect(0, 0, tw, th);
      l.globalCompositeOperation = 'multiply';
      if (mirror) { l.save(); l.translate(tw, 0); l.scale(-1, 1); }
      l.drawImage(foil, 0, 0, tw, th);
      if (mirror) l.restore();
      l.globalCompositeOperation = 'destination-in'; drawInk(l);
      g.drawImage(L, 0, 0);
      // cut the pouch outline
      g.globalCompositeOperation = 'destination-in';
      if (mirror) { g.save(); g.translate(tw, 0); g.scale(-1, 1); }
      g.drawImage(sil, 0, 0, tw, th);
      if (mirror) g.restore();
      g.globalCompositeOperation = 'source-over';
      return c;
    };
    const front = printed(l => l.drawImage(label, 0, 0, tw, th), false);
    // back of the pouch: wordmark, mascot and a few lines, set in the site's own type
    const wmPath = document.querySelector('.fw-wm path'), maPath = document.querySelector('.fw-mascot path');
    try { await Promise.all([document.fonts.load(`900 italic ${Math.round(tw * 0.05)}px Montserrat`), document.fonts.load(`800 ${Math.round(tw * 0.04)}px Montserrat`)]); } catch (e) {}
    const fam = getComputedStyle(document.body).fontFamily || 'Montserrat, sans-serif';
    const back = printed(l => {
      l.save();
      if (wmPath) { const p = new Path2D(wmPath.getAttribute('d')), s = tw * 0.5 / 800; l.setTransform(s, 0, 0, s, tw * 0.25, th * 0.13); l.fill(p, 'evenodd'); l.setTransform(1, 0, 0, 1, 0, 0); }
      if (maPath) { const p = new Path2D(maPath.getAttribute('d')), s = tw * 0.36 / 266; l.setTransform(s, 0, 0, s, tw * 0.32, th * 0.34); l.fill(p, 'evenodd'); l.setTransform(1, 0, 0, 1, 0, 0); }
      l.textAlign = 'center'; l.textBaseline = 'middle';
      l.font = `800 ${Math.round(tw * 0.047)}px ${fam}`; l.fillText('PROTEIN COFFEE CREAMER', tw / 2, th * 0.66);
      l.font = `700 ${Math.round(tw * 0.03)}px ${fam}`; l.fillText('500 g · 20 SERVINGS · 9 g PROTEIN PER SCOOP', tw / 2, th * 0.72);
      l.fillRect(tw * 0.2, th * 0.765, tw * 0.6, Math.max(2, tw * 0.002));
      l.font = `900 italic ${Math.round(tw * 0.05)}px ${fam}`; l.fillText('stir it. whip it. sip it.', tw / 2, th * 0.81);
      l.font = `800 ${Math.round(tw * 0.028)}px ${fam}`; l.fillText('GETFOWM.COM', tw / 2, th * 0.88);
      l.restore();
    }, true);

    // wrinkles: a normal map from the foil's own creases
    const nw = 320, nh = Math.round(nw * 1045 / 840), nc = mk(nw, nh), ng = nc.getContext('2d');
    ng.drawImage(foil, 0, 0, nw, nh);
    const blur = mk(nw, nh), bg = blur.getContext('2d'); bg.filter = 'blur(6px)'; bg.drawImage(foil, 0, 0, nw, nh);
    const A = ng.getImageData(0, 0, nw, nh).data, B = bg.getImageData(0, 0, nw, nh).data;
    const hgt = new Float32Array(nw * nh);
    for (let i = 0; i < nw * nh; i++) hgt[i] = ((A[4 * i] + A[4 * i + 1] + A[4 * i + 2]) - (B[4 * i] + B[4 * i + 1] + B[4 * i + 2])) / 765;
    const out = ng.createImageData(nw, nh);
    for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
      const i = y * nw + x, xl = hgt[y * nw + Math.max(0, x - 1)], xr = hgt[y * nw + Math.min(nw - 1, x + 1)], yu = hgt[Math.max(0, y - 1) * nw + x], yd = hgt[Math.min(nh - 1, y + 1) * nw + x];
      let nx = (xl - xr) * 6, ny = (yd - yu) * 6, nz = 1; const len = Math.hypot(nx, ny, nz); nx /= len; ny /= len; nz /= len;
      out.data[4 * i] = (nx * 0.5 + 0.5) * 255; out.data[4 * i + 1] = (ny * 0.5 + 0.5) * 255; out.data[4 * i + 2] = (nz * 0.5 + 0.5) * 255; out.data[4 * i + 3] = 255;
    }
    ng.putImageData(out, 0, 0);
    const normalTex = canvasTex(nc, false);

    const msaa = renderer.capabilities.isWebGL2;
    const mat = map => new THREE.MeshPhysicalMaterial({
      map, emissiveMap: map, emissive: new THREE.Color('#ffffff'), emissiveIntensity: 0.25,
      normalMap: normalTex, normalScale: new THREE.Vector2(1.1, 1.1),
      metalness: 0.26, roughness: 0.29, clearcoat: 0.82, clearcoatRoughness: 0.21, envMapIntensity: 1.12,
      alphaTest: 0.5, alphaToCoverage: msaa, side: THREE.FrontSide
    });
    const frontTex = canvasTex(front), backTex = canvasTex(back);

    // fullness: thickest at the gusset, flat at the top seal, zero at the side seals
    const depth = (u, v) => {
      const t = clamp(Math.abs(2 * u - 1) / 0.93);
      const prof = t >= 1 ? 0 : Math.pow(1 - Math.pow(t, 2.6), 0.5);
      const g = 0.27 * Math.pow(1 - smooth(0.36, 0.885, v), 0.9) * (0.88 + 0.12 * smooth(0, 0.12, v));
      const zip = 0.013 * Math.exp(-Math.pow((v - 0.906) / 0.008, 2)) * (t < 0.98 ? 1 : 0);
      return g * prof + zip;
    };
    const panel = (tex, isBack) => {
      const geo = new THREE.PlaneGeometry(PW, PH, small ? 44 : 64, small ? 64 : 96);
      const p = geo.attributes.position, uv = geo.attributes.uv;
      for (let i = 0; i < p.count; i++) {
        const u = uv.getX(i), v = uv.getY(i), d = depth(u, v);
        p.setZ(i, d);
        p.setX(i, p.getX(i) * (1 - 0.035 * d / 0.27 * (1 - smooth(0.85, 1, v))));
      }
      geo.computeVertexNormals();
      if (isBack) geo.rotateY(Math.PI);
      return new THREE.Mesh(geo, mat(tex));
    };
    const g = new THREE.Group();
    const frontM = panel(frontTex, false), backM = panel(backTex, true);
    g.add(frontM, backM);
    // the gusset underneath
    const sh = new THREE.Shape(), N = 40;
    for (let i = 0; i <= N; i++) { const u = 0.04 + 0.92 * i / N; const x = (u - 0.5) * PW, z = depth(u, 0.004); i ? sh.lineTo(x, z) : sh.moveTo(x, z); }
    for (let i = N; i >= 0; i--) { const u = 0.04 + 0.92 * i / N; sh.lineTo((u - 0.5) * PW, -depth(u, 0.004)); }
    const base = new THREE.Mesh(new THREE.ShapeGeometry(sh), new THREE.MeshPhysicalMaterial({ color: '#cdb79c', metalness: 0.4, roughness: 0.4 }));
    base.rotation.x = Math.PI / 2; base.position.y = -PH / 2 + 0.012; g.add(base);
    g.userData = { front: frontM, back: backM };
    return g;
  }

  /* ---------- drinks ---------- */
  function lathe(points, segs = 64) { return new THREE.LatheGeometry(points.map(([x, y]) => new THREE.Vector2(x, y)), segs); }
  const glassMat = new THREE.MeshPhysicalMaterial({ color: '#ffffff', metalness: 0, roughness: 0.03, transparent: true, opacity: 0.06, clearcoat: 1, clearcoatRoughness: 0.03, envMapIntensity: 2.4, specularIntensity: 1, depthWrite: false, side: THREE.DoubleSide });
  const rimMat = new THREE.MeshBasicMaterial({ color: '#fffaf3', transparent: true, opacity: 0.5, depthWrite: false });
  const baseMat = new THREE.MeshBasicMaterial({ color: '#fffaf3', transparent: true, opacity: 0.16, depthWrite: false });
  const GH = 2.05, GR0 = 0.5, GR1 = 0.64, GT = 0.035; // tumbler: height, bottom/top radius, wall
    function buildDrink() {
    const g = new THREE.Group();
    const st = { iced: 1, foamH: 0.3, foamOn: 1, foamTarget: 0.3, foamOnTarget: 1, fill: 0.6, kind: 'freddo', hot: 0, mug: 0, swirl: 0, straw: 1 };
    // ---- iced: tumbler ----
    const iced = new THREE.Group(); g.add(iced);
    const glassPts = [[0, -GH / 2], [GR0 - 0.04, -GH / 2], [GR0, -GH / 2 + 0.04], [GR1, GH / 2], [GR1 - GT, GH / 2], [GR0 - GT, -GH / 2 + 0.16], [0, -GH / 2 + 0.16]];
    const glass = new THREE.Mesh(lathe(glassPts, 72), glassMat); glass.renderOrder = 5; iced.add(glass);
    const rimRing = new THREE.Mesh(new THREE.TorusGeometry(GR1 - GT / 2, 0.012, 8, 96), rimMat); rimRing.rotation.x = Math.PI / 2; rimRing.position.y = GH / 2; rimRing.renderOrder = 6; iced.add(rimRing);
    const baseRing = new THREE.Mesh(new THREE.TorusGeometry(GR0 - 0.03, 0.02, 8, 96), baseMat); baseRing.rotation.x = Math.PI / 2; baseRing.position.y = -GH / 2 + 0.03; baseRing.renderOrder = 6; iced.add(baseRing);
    // liquid: a lathe that follows the inside of the glass, rebuilt when the fill level changes
    const Y0 = -GH / 2 + 0.16;
    const ir = y => lerp(GR0 - GT, GR1 - GT, (y - Y0) / (GH / 2 - Y0)) - 0.008;
    const liqMat = new THREE.MeshPhysicalMaterial({ vertexColors: true, roughness: 0.22, metalness: 0, clearcoat: 0.35, clearcoatRoughness: 0.2, envMapIntensity: 0.55 });
    const liquid = new THREE.Mesh(new THREE.BufferGeometry(), liqMat); iced.add(liquid);
    let liqFill = -1, liqCols = null;
    function buildLiquid(fill, cols) {
      const top = Y0 + (GH / 2 - 0.05 - Y0) * fill, pts = [[0, Y0]];
      for (let i = 0; i <= 30; i++) { const y = Y0 + (top - Y0) * i / 30; pts.push([ir(y), y]); }
      pts.push([0, top]);
      const geo = lathe(pts, 64), pos = geo.attributes.position, col = new Float32Array(pos.count * 3);
      const B = new THREE.Color(cols[0]), T = new THREE.Color(cols[1]), M = new THREE.Color(cols[2]), at = cols[3], c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const t = (pos.getY(i) - Y0) / (top - Y0);
        if (t < at) c.copy(B).lerp(M, smooth(0, at, t)); else c.copy(M).lerp(T, smooth(at, 1, t));
        col[3 * i] = c.r; col[3 * i + 1] = c.g; col[3 * i + 2] = c.b;
      }
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      liquid.geometry.dispose(); liquid.geometry = geo; liqFill = fill; liqCols = cols;
      return top;
    }
    const setColors = (a, b, c, d) => buildLiquid(st.fill, [a, b, c, d]);
    // flat top of the liquid
    const liqTop = new THREE.Mesh(new THREE.CircleGeometry(1, 64), new THREE.MeshPhysicalMaterial({ color: '#4a2716', roughness: 0.12, clearcoat: 0.8 }));
    liqTop.rotation.x = -Math.PI / 2; iced.add(liqTop);
    // ice
    const iceMat = new THREE.MeshPhysicalMaterial({ color: '#fbf7f2', roughness: 0.06, metalness: 0, transparent: true, opacity: 0.42, clearcoat: 1, envMapIntensity: 1.6 });
    const ice = []; const r = rnd(5);
    for (let i = 0; i < 5; i++) { const m = new THREE.Mesh(new RoundedBoxGeometry(0.36, 0.34, 0.36, 3, 0.07), iceMat); m.rotation.set(r() * 2, r() * 3, r() * 2); m.userData.a = i / 5 * Math.PI * 2 + r() * 0.5; m.userData.k = r(); ice.push(m); iced.add(m); }
    // straw
    const straw = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 2.2, 20, 1, true), new THREE.MeshStandardMaterial({ color: C.ink, roughness: 0.5, side: THREE.DoubleSide }));
    straw.rotation.z = -0.24; straw.position.set(0.2, 0.55, -0.1); iced.add(straw);
    // ---- hot: cup + saucer ----
    const hot = new THREE.Group(); g.add(hot);
    const glaze = new THREE.MeshPhysicalMaterial({ color: '#f1e6d6', roughness: 0.22, clearcoat: 0.9, clearcoatRoughness: 0.1 });
    const glazeInk = new THREE.MeshPhysicalMaterial({ color: C.ink, roughness: 0.28, clearcoat: 0.8, clearcoatRoughness: 0.15 });
    const cupPts = [[0, -0.55], [0.36, -0.55], [0.42, -0.5], [0.56, -0.2], [0.66, 0.2], [0.7, 0.5], [0.66, 0.5], [0.62, 0.2], [0.52, -0.2], [0.38, -0.44], [0, -0.44]];
    const cup = new THREE.Mesh(lathe(cupPts, 72), glaze); hot.add(cup);
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.055, 16, 40, Math.PI * 1.25), glaze); handle.position.set(0.68, 0.02, 0); handle.rotation.z = -Math.PI * 0.62; hot.add(handle);
    const saucer = new THREE.Mesh(lathe([[0, -0.62], [0.9, -0.62], [1.08, -0.52], [1.06, -0.5], [0.88, -0.58], [0, -0.58]], 72), glaze); hot.add(saucer);
    const mug = new THREE.Mesh(lathe([[0, -0.62], [0.5, -0.62], [0.56, -0.56], [0.6, 0.62], [0.55, 0.62], [0.52, -0.5], [0, -0.5]], 72), glazeInk); hot.add(mug);
    const mugHandle = new THREE.Mesh(new THREE.TorusGeometry(0.26, 0.07, 16, 40, Math.PI * 1.15), glazeInk); mugHandle.position.set(0.6, 0.02, 0); mugHandle.rotation.z = -Math.PI * 0.57; hot.add(mugHandle);
    const surface = new THREE.Mesh(new THREE.CircleGeometry(1, 64), new THREE.MeshPhysicalMaterial({ color: '#8b5a36', roughness: 0.2, clearcoat: 0.7 }));
    surface.rotation.x = -Math.PI / 2; hot.add(surface);
    // ---- foam (shared): a soft dome with little bubbles ----
    const domePts = []; for (let i = 0; i <= 24; i++) { const a = i / 24 * Math.PI / 2; domePts.push([Math.cos(a), Math.sin(a) * 0.55 + 0.45]); }
    domePts.unshift([1, 0]); domePts.unshift([0, 0]);
    const domeGeo = lathe(domePts.map(([x, y], i) => i === 0 ? [0, 0] : [x, y]), 72);
    { const p = domeGeo.attributes.position, rr = rnd(3); for (let i = 0; i < p.count; i++) { const x = p.getX(i), y = p.getY(i), z = p.getZ(i); if (y > 0.5) { const k = 1 + (Math.sin(x * 9 + z * 7) * 0.02 + Math.sin(x * 23 - z * 19) * 0.012) + (rr() - 0.5) * 0.01; p.setY(i, 0.45 + (y - 0.45) * k); } } domeGeo.computeVertexNormals(); }
    const foamMat = new THREE.MeshStandardMaterial({ color: '#fdf8f1', emissive: new THREE.Color('#fff6ea'), emissiveIntensity: 0.12, roughness: 0.95, metalness: 0, bumpMap: foamBump, bumpScale: 5, envMapIntensity: 0.5 });
    foamBump.repeat.set(2, 2);
    const foam = new THREE.Mesh(domeGeo, foamMat); g.add(foam);
    const shadow = contactShadow(2.3, 2.3); g.add(shadow);
    
    g.userData = {
      st, iced, hot, cup, handle, saucer, mug, mugHandle, foam, ice, straw, liquid, liqTop, surface, setColors, shadow,
      liquidTop: () => Y0 + (GH / 2 - 0.05 - Y0) * liqFill, innerR: ir,
      // place everything for the current state (called every frame while animating)
      layout(t) {
        const isHot = st.hot > 0.5;
        if (liqFill !== st.fill && liqCols) buildLiquid(st.fill, liqCols);
        const fillY = Y0 + (GH / 2 - 0.05 - Y0) * st.fill;
        iced.visible = st.hot < 0.5; hot.visible = st.hot >= 0.5;
        // iced
        liqTop.position.y = fillY; liqTop.scale.setScalar(ir(fillY));
        ice.forEach((m, i) => { const a = m.userData.a + (reduce ? 0 : t * 0.15), rr = (ir(fillY) - 0.24) * (0.45 + 0.4 * m.userData.k); m.position.set(Math.cos(a) * rr, fillY - 0.1 - m.userData.k * 0.3 + Math.sin(t * 1.3 + i) * 0.012, Math.sin(a) * rr); m.visible = st.iced > 0.5; });
        straw.visible = st.straw > 0.01; straw.position.y = 0.45 + (1 - st.straw) * 1.6;
        // hot
        cup.visible = handle.visible = saucer.visible = st.mug < 0.5; mug.visible = mugHandle.visible = st.mug >= 0.5;
        const hotFill = st.mug >= 0.5 ? 0.5 : 0.38, hotR = st.mug >= 0.5 ? 0.54 : 0.635;
        surface.position.y = hotFill; surface.scale.setScalar(hotR);
        // foam sits on whatever is underneath
        const fh = Math.max(0.0001, st.foamH * st.foamOn);
        if (isHot) { foam.position.y = hotFill - 0.02; foam.scale.set(hotR - 0.01, fh * 0.55, hotR - 0.01); }
        else { const rTop = ir(fillY + fh * 0.5); foam.position.y = fillY - 0.01; foam.scale.set(rTop, fh, rTop); }
        foam.visible = st.foamOn * st.foamH > 0.004;
        foam.rotation.y = st.swirl;
        shadow.position.y = isHot ? -0.62 : -GH / 2 + 0.005; shadow.scale.set(isHot ? 1.2 : 1, 1, 1);
      }
    };
    return g;
  }

  /* ---------- scoop, frother, powder (story) ---------- */
  function buildScoop() {
    const g = new THREE.Group(), m = new THREE.MeshPhysicalMaterial({ color: '#f3e9db', roughness: 0.35, clearcoat: 0.4 });
    const bowl = new THREE.Mesh(lathe([[0, -0.16], [0.2, -0.16], [0.24, -0.1], [0.25, 0.12], [0.225, 0.12], [0.215, -0.1], [0.18, -0.13], [0, -0.13]], 48), m); g.add(bowl);
    const handle = new THREE.Mesh(new RoundedBoxGeometry(0.72, 0.06, 0.12, 2, 0.03), m); handle.position.set(-0.55, 0.06, 0); handle.rotation.z = 0.22; g.add(handle);
    const pow = new THREE.Mesh(new THREE.SphereGeometry(0.22, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: '#efe1cd', roughness: 1, bumpMap: powderBump, bumpScale: 4 }));
    pow.scale.set(1, 0.55, 1); pow.position.y = 0.1; g.add(pow);
    g.userData = { pow };
    return g;
  }
  function buildFrother() {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.11, 0.62, 8, 24), new THREE.MeshPhysicalMaterial({ color: C.ink, roughness: 0.3, clearcoat: 0.8 }));
    body.position.y = 1.15; g.add(body);
    const steel = new THREE.MeshStandardMaterial({ color: '#d9d4cc', metalness: 1, roughness: 0.22 });
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 1.0, 12), steel); shaft.position.y = 0.42; g.add(shaft);
    const whisk = new THREE.Group(); whisk.position.y = -0.08; g.add(whisk);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.014, 10, 40), steel); ring.rotation.x = Math.PI / 2; whisk.add(ring);
    const coil = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.022, 8, 60, Math.PI * 2), steel); coil.rotation.x = Math.PI / 2; coil.scale.z = 0.4; whisk.add(coil);
    g.userData = { whisk };
    return g;
  }
  const POW_N = small ? 90 : 160;
  const powder = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.022, 0), new THREE.MeshStandardMaterial({ color: '#f1e5d3', roughness: 1 }), POW_N);
  powder.frustumCulled = false;
  const powSeed = Array.from({ length: POW_N }, (_, i) => { const r = rnd(100 + i); return { d: r(), a: r() * Math.PI * 2, s: 0.5 + r(), x: (r() - 0.5) * 0.18, z: (r() - 0.5) * 0.18 }; });

  /* ---------- build everything ---------- */
  let pouch;
  try { pouch = await buildPouch(); } catch (e) { console.warn('[fowm] pouch textures failed', e); fail(); return; }
  const pouchShadow = contactShadow(2.4, 1.1);
  const pouchRoot = new THREE.Group(), pouchSpin = new THREE.Group();
  pouchRoot.add(pouchSpin); pouchSpin.add(pouch); pouchRoot.add(pouchShadow); pouchShadow.position.y = -PH / 2 - 0.02;
  const drink = buildDrink(), drinkRoot = new THREE.Group(), drinkSpin = new THREE.Group();
  drinkRoot.add(drinkSpin); drinkSpin.add(drink);
  const scoop = buildScoop(), frother = buildFrother();
  scene.add(pouchRoot, drinkRoot, scoop, frother, powder);
  const D = drink.userData;

  /* drink presets */
  const DRINKS = {
    freddo: { hot: 0, iced: 1, mug: 0, fill: 0.6, cols: ['#180a04', '#6b3a1c', '#2b1408', 0.55], stir: ['#4a2a17', '#a87a55', '#7c5135', 0.5], top: '#7a4422', stirTop: '#b48660' },
    latte: { hot: 0, iced: 1, mug: 0, fill: 0.68, cols: ['#f1e7da', '#4e2914', '#d2b28f', 0.62], stir: ['#9c6c45', '#c79f78', '#b48a64', 0.5], top: '#5e321a', stirTop: '#c9a17a' },
    cappuccino: { hot: 1, iced: 0, mug: 0, surf: '#8b5a36', stirSurf: '#b98a62' },
    filter: { hot: 1, iced: 0, mug: 1, surf: '#2a160c', stirSurf: '#8f6142' }
  };
  const dState = { drink: 'freddo', scoops: 1, style: 'foam' };
  function applyDrink(s, instant) {
    const P = DRINKS[s.drink] || DRINKS.freddo, stir = s.style === 'stir';
    D.st.hot = P.hot; D.st.iced = P.iced; D.st.mug = P.mug;
    D.st.fill = P.fill || 0.6;
    if (!P.hot) { const c = stir ? P.stir : P.cols; D.setColors(c[0], c[1], c[2], c[3]); D.liqTop.material.color.set(stir ? P.stirTop : P.top); }
    else if (!D.liquid.geometry.attributes.position) { const c = DRINKS.freddo.cols; D.setColors(c[0], c[1], c[2], c[3]); }
    else D.surface.material.color.set(stir ? P.stirSurf : P.surf);
    D.st.foamTarget = stir ? (P.hot ? 0.06 : 0.0) : (s.scoops === 2 ? 0.42 : 0.28);
    D.st.foamOnTarget = stir && !P.hot ? 0 : 1;
    if (instant) { D.st.foamH = D.st.foamTarget; D.st.foamOn = D.st.foamOnTarget; }
    D.st.straw = P.hot ? 0 : 1;
  }
  applyDrink(dState, true);
  document.addEventListener('fw:build', e => { Object.assign(dState, e.detail); if (cur.stage === 'build') { applyDrink(dState); bump(); } });

  /* ---------- labels (how it works) ---------- */
  const labelsEl = document.getElementById('fw-labels');
  const SVGNS = 'http://www.w3.org/2000/svg';
  const lineSvg = document.createElementNS(SVGNS, 'svg'); lineSvg.setAttribute('class', 'fw-tag-lines'); labelsEl.appendChild(lineSvg);
  const LABELS = [
    { text: '1 scoop · 25 g', sub: '9 g protein', side: 'right' },
    { text: 'cold foam', sub: 'thick and creamy', side: 'right' },
    { text: 'no sweeteners', sub: 'just coffee + fowm', side: 'right' }
  ].map(L => {
    const el = document.createElement('div'); el.className = 'fw-tag'; el.innerHTML = `${L.text}<small>${L.sub}</small>`; labelsEl.appendChild(el);
    const ln = document.createElementNS(SVGNS, 'line'), dot = document.createElementNS(SVGNS, 'circle'); dot.setAttribute('r', '4.5');
    lineSvg.append(ln, dot);
    return Object.assign(L, { el, ln, dot, op: 0, w: 0 });
  });

  /* ---------- stage tracking ---------- */
  let W = innerWidth, H = innerHeight, CH = H, allocW = 0, allocH = 0;
  const lvh = document.createElement('div'); lvh.style.cssText = 'position:fixed;left:0;top:0;width:0;height:100vh;height:100lvh;visibility:hidden;pointer-events:none'; document.body.appendChild(lvh);
  function resize() {
    W = innerWidth; H = innerHeight; const ch = Math.max(H, lvh.offsetHeight || 0);
    if (W !== allocW || Math.abs(ch - allocH) > 1) { allocW = W; allocH = CH = ch; renderer.setSize(W, CH, false); canvas.style.width = W + 'px'; canvas.style.height = CH + 'px'; camera.aspect = W / CH; camera.updateProjectionMatrix(); }
    bump();
  }
  const VIS_H = 2 * camera.position.z * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const tv = new THREE.Vector3();
  const toWorld = (px, py) => { tv.set(px / W * 2 - 1, -(py / CH) * 2 + 1, 0.5).unproject(camera).sub(camera.position).normalize(); const d = -camera.position.z / tv.z; return camera.position.clone().add(tv.multiplyScalar(d)); };
  const toScreen = v => { tv.copy(v).project(camera); return [(tv.x + 1) / 2 * W, (1 - tv.y) / 2 * CH]; };
  const pxPerWorld = () => CH / VIS_H;
  function pickStage() {
    let best = null, bd = Infinity;
    for (const el of stageEls) {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.bottom < 0 || r.top > H) continue;
      const d = Math.abs((r.top + r.bottom) / 2 - H / 2);
      if (d < bd) { bd = d; best = el; }
    }
    return best;
  }

  /* ---------- interaction ---------- */
  let mx = 0, my = 0, drag = 0, dragVel = 0, dragging = null, lastX = 0, lastMove = 0, lastScroll = 0, needs = true;
  const bump = () => { needs = true; };
  addEventListener('resize', resize); addEventListener('orientationchange', () => setTimeout(resize, 250));
  addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
  addEventListener('pointermove', e => {
    mx = e.clientX / W * 2 - 1; my = e.clientY / H * 2 - 1; lastMove = performance.now();
    if (dragging !== null && e.pointerId === dragging) { const dx = e.clientX - lastX; lastX = e.clientX; dragVel = dx * 0.011; drag += dragVel; }
  }, { passive: true });
  stageEls.filter(el => el.hasAttribute('data-drag')).forEach(el => el.addEventListener('pointerdown', e => {
    dragging = e.pointerId; lastX = e.clientX; dragVel = 0;
    const hint = el.querySelector('.fw-hint'); if (hint) hint.style.opacity = '0';
  }));
  addEventListener('pointerup', () => { dragging = null; }); addEventListener('pointercancel', () => { dragging = null; });

  /* ---------- frame loop ---------- */
  const cur = { stage: null, appear: 0, pos: V3(0, 0, 0), scale: 1, snap: true, leaving: false };
  let story = { p: FW.storyP || 0, s: FW.storyP || 0 };
  const storySec = document.querySelector('[data-fw-story]');
  let last = performance.now(), idle = 0, frameN = 0, opLast = '';
  const smoothScrollY = { v: scrollY };
  resize();
  doc.classList.add('fw-3d'); doc.classList.remove('fw-no-3d');

  function storyLayout(r, e) {
    // r: stage rect; e: eased progress. Returns positions in world space.
    const wide = W > 900;
    const size = Math.min(r.width, r.height * (wide ? 1.25 : 0.95));
    const S = size / CH * VIS_H / (wide ? 3.6 : 2.35);
    const c = toWorld(r.left + r.width * (wide ? 0.5 : 0.5), r.top + r.height * (wide ? 0.54 : 0.52));
    return { S, c, wide };
  }

  function frame(now) {
    const dt = Math.min(0.1, Math.max(0, (now - last) / 1000)); last = now; frameN++;
    const t = now / 1000;
    const el = pickStage(), want = el ? el.dataset.stage : null;
    if (want !== cur.stage && !cur.leaving) { if (cur.appear < 0.04 || !cur.stage) switchTo(want); else cur.leaving = true; }
    if (cur.leaving && cur.appear < 0.04) { cur.leaving = false; switchTo(want); }
    const name = cur.stage, sEl = stageEls.find(x => x.dataset.stage === name) || null;
    const r = sEl ? sEl.getBoundingClientRect() : null;
    if (!reduce) idle += dt;
    if (dragging === null) { dragVel *= Math.pow(0.03, dt); drag += dragVel; }
    smoothScrollY.v += (scrollY - smoothScrollY.v) * (1 - Math.exp(-dt * 8));
    let busy = needs || now - lastScroll < 350 || now - lastMove < 350 || dragging !== null || Math.abs(dragVel) > 1e-4;
    needs = false;

    // visibility of the current stage decides fade
    let apTarget = 0;
    if (r && r.width > 0) {
      const vis = clamp((Math.min(r.bottom, H) - Math.max(r.top, 0)) / Math.min(r.height, H));
      apTarget = cur.leaving ? 0 : clamp((vis - 0.1) / 0.3);
    }
    cur.appear += (apTarget - cur.appear) * (1 - Math.exp(-dt * (cur.leaving ? 14 : 6)));
    if (Math.abs(apTarget - cur.appear) > 0.003) busy = true;
    const ap = cur.appear * cur.appear * (3 - 2 * cur.appear);

    pouchRoot.visible = drinkRoot.visible = scoop.visible = frother.visible = powder.visible = false;
    let labelOn = false;
    if (r && r.width > 0 && name) {
      if (name === 'story') {
        // ----- scoop to foam -----
        const sp = storySec ? clamp(-storySec.getBoundingClientRect().top / Math.max(1, storySec.offsetHeight - H)) : 0;
        story.p = reduce ? 1 : sp;
        const k = cur.snap ? 1 : 1 - Math.exp(-dt * 10);
        story.s += (story.p - story.s) * k; if (Math.abs(story.p - story.s) > 0.0005) busy = true;
        const p = story.s, L = storyLayout(r, p), S = L.S;
        // pouch on the left, tipping toward the glass while the scoop comes out
        const pOut = smooth(0.06, 0.2, p), back = smooth(0.7, 0.86, p);
        pouchRoot.visible = true; pouchRoot.scale.setScalar(S * (L.wide ? 0.78 : 0.6) * (1 - 0.18 * back));
        pouchRoot.position.copy(L.c).add(V3((L.wide ? -1.15 : -0.62) * S - back * 0.25 * S, (L.wide ? -0.05 : 0.05) * S + back * 0.1 * S, -back * 0.5));
        pouchSpin.rotation.set(0.04, 0.42 - pOut * 0.15 + Math.sin(t * 0.6) * 0.03, -0.1 * pOut * (1 - back));
        pouchShadow.material.opacity = 0.65;
        // the glass of iced coffee
        drinkRoot.visible = true;
        const gx = (L.wide ? 0.82 : 0.5) * S, gy = (L.wide ? -0.32 : -0.08) * S, center = smooth(0.74, 0.92, p);
        drinkRoot.scale.setScalar(S * (L.wide ? 0.95 : 0.66) * (1 + 0.14 * center));
        drinkRoot.position.copy(L.c).add(V3(lerp(gx, (L.wide ? 0.45 : 0.3) * S, center), gy, 0));
        drinkSpin.rotation.set(0.12, -0.35 + p * 0.9 + drag * 0.6, 0);
        if (D.st.kind !== 'story') { applyDrink({ drink: 'freddo', scoops: 1, style: 'foam' }, true); D.st.kind = 'story'; }
        D.st.foamOn = 1; D.st.foamH = 0.36 * ease(smooth(0.46, 0.68, p)); D.st.straw = smooth(0.78, 0.9, p);
        D.st.swirl = p * 9;
        // scoop: rises out of the pouch, arcs over the glass, tips
        const sUp = smooth(0.06, 0.16, p), sMove = smooth(0.14, 0.26, p), sTip = smooth(0.24, 0.3, p), sGone = smooth(0.38, 0.44, p);
        scoop.visible = sUp > 0.001 && sGone < 0.999;
        const pTop = pouchRoot.position.clone().add(V3(0.1 * S, PH * 0.5 * pouchRoot.scale.y + 0.25 * S * sUp, 0.3));
        const gTop = drinkRoot.position.clone().add(V3(-0.05 * S, (GH * 0.5 + 0.55) * drinkRoot.scale.y, 0.15));
        const sp2 = pTop.clone().lerp(gTop, sMove); sp2.y += Math.sin(sMove * Math.PI) * 0.6 * S;
        scoop.position.copy(sp2); scoop.scale.setScalar(S * 0.8 * (1 - sGone));
        scoop.rotation.set(0.2, -0.5, -sTip * 1.9);
        scoop.userData.pow.visible = sTip < 0.6;
        // powder falling into the glass
        const fallP = (p - 0.25) / 0.14;
        if (fallP > 0 && fallP < 1.4) {
          powder.visible = true; const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pos = new THREE.Vector3();
          const top = gTop.clone().add(V3(0.08 * S, -0.05 * S, 0)), floor = drinkRoot.position.y + (GH / 2 - 0.5) * drinkRoot.scale.y;
          for (let i = 0; i < POW_N; i++) {
            const ps = powSeed[i], lt = fallP - ps.d * 0.55;
            if (lt < 0 || lt > 0.75) { sc.set(0, 0, 0); } else {
              const y = top.y - 4.2 * lt * lt * S; pos.set(top.x + ps.x * S + Math.sin(ps.a) * lt * 0.12 * S, Math.max(floor, y), top.z + ps.z * S);
              sc.setScalar(S * ps.s * (y < floor ? 0 : 1));
            }
            m4.compose(pos, q, sc); powder.setMatrixAt(i, m4);
          }
          powder.instanceMatrix.needsUpdate = true;
        }
        // frother whips the foam
        const fIn = smooth(0.4, 0.47, p), fOut = smooth(0.66, 0.72, p);
        frother.visible = fIn > 0.001 && fOut < 0.999;
        frother.scale.setScalar(S * 0.72);
        frother.position.copy(drinkRoot.position).add(V3(0, (D.liquidTop() + 0.12) * drinkRoot.scale.y + (1 - fIn) * 2.4 * S + fOut * 2.6 * S, 0.05));
        frother.rotation.set(0, 0, 0.05);
        frother.userData.whisk.rotation.y = reduce ? 0 : t * 40 * (fIn * (1 - fOut));
        // labels
        labelOn = true;
        const lt = [smooth(0.1, 0.16, p) * (1 - smooth(0.34, 0.4, p)), smooth(0.52, 0.58, p) * (1 - smooth(0.72, 0.78, p)), smooth(0.8, 0.86, p)];
        const anchors = [scoop.position.clone(), drinkRoot.position.clone().add(V3(0.32 * drinkRoot.scale.x, (D.liquidTop() + D.st.foamH * 0.75) * drinkRoot.scale.y, 0)), drinkRoot.position.clone().add(V3(0.45 * drinkRoot.scale.x, -0.1 * drinkRoot.scale.y, 0))];
        placeLabels(anchors, lt.map(v => v * ap), L.wide);
        cur.pos.copy(L.c);
      } else {
        // ----- one actor centred on its stage -----
        const fit = +sEl.dataset.fit || 2.4, size = Math.min(r.width, r.height);
        const tgtScale = size / CH * VIS_H / fit, tgtPos = toWorld(r.left + r.width / 2, r.top + r.height * 0.5);
        const k = cur.snap ? 1 : 1 - Math.exp(-dt * 14);
        if (cur.pos.distanceTo(tgtPos) > 0.001 * tgtScale || Math.abs(tgtScale - cur.scale) > 0.001 * tgtScale) busy = true;
        cur.pos.lerp(tgtPos, k); cur.scale += (tgtScale - cur.scale) * k;
        const bob = reduce ? 0 : Math.sin(t * 1.1) * 0.035;
        if (name === 'build') {
          drinkRoot.visible = true;
          drinkRoot.position.copy(cur.pos).add(V3(0, (bob - 0.05) * cur.scale, 0));
          drinkRoot.scale.setScalar(cur.scale * 1.05);
          drinkSpin.rotation.set(0.16 + my * 0.04, -0.5 + (reduce ? 0 : Math.sin(idle * 0.35) * 0.35) + drag, -mx * 0.03);
          // ease the foam and colours toward the chosen drink
          const f = 1 - Math.exp(-dt * 6);
          D.st.foamH += (D.st.foamTarget - D.st.foamH) * f; D.st.foamOn += (D.st.foamOnTarget - D.st.foamOn) * f;
          D.st.swirl += dt * 0.2;
          if (Math.abs(D.st.foamTarget - D.st.foamH) > 0.002 || Math.abs(D.st.foamOnTarget - D.st.foamOn) > 0.002) busy = true;
        } else {
          pouchRoot.visible = true;
          pouchRoot.position.copy(cur.pos).add(V3(0, bob * cur.scale, 0));
          pouchRoot.scale.setScalar(cur.scale);
          const scrollSpin = name === 'hero' ? smoothScrollY.v * 0.0016 : 0;
          const sway = reduce ? 0 : Math.sin(idle * 0.42) * 0.42;
          pouchSpin.rotation.set(0.05 + my * 0.05, (name === 'hero' ? -0.18 : -0.3) + sway + scrollSpin + drag, -0.04 - mx * 0.04);
          pouchShadow.material.opacity = 0.7;
          pouchShadow.position.y = -PH / 2 - 0.03 - bob;
        }
        cur.snap = false;
      }
    }
    if (!labelOn) hideLabels();
    if (scene.visible !== true) scene.visible = true;
    D.layout(t);
    // fade the canvas
    const op = ap.toFixed(3); if (op !== opLast) { canvas.style.opacity = op; opLast = op; }
    if (ap < 0.01) { pouchRoot.visible = drinkRoot.visible = scoop.visible = frother.visible = powder.visible = false; }
    const draw = busy || (!reduce && (frameN & 1) === 0 && ap > 0.01);
    if (draw) renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  function switchTo(name) {
    cur.stage = name; cur.snap = true;
    if (name === 'build') { applyDrink(dState, true); D.st.kind = 'build'; }
    bump();
  }
  function placeLabels(anchors, ops, wide) {
    LABELS.forEach((L, i) => {
      const o = ops[i] || 0;
      L.op += (o - L.op) * 0.25;
      if (L.op < 0.01) { L.el.style.opacity = 0; L.ln.style.opacity = 0; L.dot.style.opacity = 0; return; }
      const [ax, ay] = toScreen(anchors[i]);
      if (!L.w) L.w = L.el.offsetWidth || 160;
      const dir = L.side === 'left' ? -1 : 1, gap = wide ? 70 : 34;
      let lx = ax + dir * gap + (dir < 0 ? -L.w : 0), ly = ay - (wide ? 46 : 38);
      ly = Math.max(H * 0.3, ly);
      lx = Math.max(12, Math.min(W - L.w - 12, lx));
      L.el.style.transform = `translate(${lx.toFixed(1)}px, ${ly.toFixed(1)}px)`;
      L.el.style.opacity = L.op.toFixed(3);
      const ex = dir < 0 ? lx + L.w : lx, ey = ly + 16;
      L.ln.setAttribute('x1', ax.toFixed(1)); L.ln.setAttribute('y1', ay.toFixed(1)); L.ln.setAttribute('x2', ex.toFixed(1)); L.ln.setAttribute('y2', ey.toFixed(1));
      L.ln.style.opacity = (L.op * 0.9).toFixed(3);
      L.dot.setAttribute('cx', ax.toFixed(1)); L.dot.setAttribute('cy', ay.toFixed(1)); L.dot.style.opacity = L.op.toFixed(3);
    });
  }
  function hideLabels() { LABELS.forEach(L => { if (L.op > 0) { L.op = 0; L.el.style.opacity = 0; L.ln.style.opacity = 0; L.dot.style.opacity = 0; } }); }

  // compile shaders while the canvas is still invisible, then start
  pouchRoot.visible = drinkRoot.visible = scoop.visible = frother.visible = powder.visible = true;
  try { renderer.compile(scene, camera); } catch (e) {}
  requestAnimationFrame(frame);
  FW.three = { scene, renderer, camera, pouch, drink, applyDrink, get stage() { return cur.stage; } };
  document.dispatchEvent(new CustomEvent('fw:3d-ready'));
}

// start after the page has painted, so text shows first
const start = () => main().catch(err => { console.warn('[fowm] 3D off:', err); fail(); });
if (document.readyState === 'complete') requestAnimationFrame(() => setTimeout(start, 0));
else addEventListener('load', () => requestAnimationFrame(() => setTimeout(start, 0)), { once: true });